import { useEffect, useLayoutEffect, useRef } from "react"
import { createPortal } from "react-dom"
import type { CSSProperties, RefObject } from "react"
import DeckCard from "@/components/DeckBrowser/DeckCard"
import PatternDemo from "@/components/DeckBrowser/PatternDemo"
import { morphClose, morphOpen } from "@/components/DeckBrowser/deckMorph"
import { cardFace, parseCardId } from "@/content/cards"
import { DECK_DETAIL_COPY } from "@/content/cardsRound"
import { patternsForCard } from "@/content/patterns"
import type { CardId } from "@/types"
import styles from "@/styles/components/DeckDetail.module.css"

/*
 * The capability detail overlay — ported from the handoff's `.ov` / `.sheet`.
 *
 * Browser-only (renders `DeckCard`, never the in-session `CardFace`). It replaces
 * the old side panel: a pill with the capability name, the statement as the
 * headline, three short sections, the card art, and "See it in action". Tech
 * stack and the old worked examples are intentionally gone.
 *
 * Opening and closing is the card morph (see `deckMorph`): a clone of the clicked
 * card grows from its on-screen rect into the sheet, flipping to the paper back,
 * and shrinks back on close. `sourceEl` is that card.
 *
 * "See it in action" is live, from `content/patterns` (the Figma card→pattern
 * map plus the handoff's per-pattern copy), with a `PatternDemo` per pattern. The
 * three sections still render skeletons — that per-card body copy is a later
 * content PR.
 */

const RM =
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches

function Skeleton({ width }: { width?: string }) {
  return <div className={styles.sk} style={width ? { width } : undefined} aria-hidden="true" />
}

interface DeckDetailProps {
  cardId: CardId
  /** Ref to the clicked card — the morph grows from and shrinks back into it. */
  sourceRef: RefObject<HTMLElement | null>
  onClose: () => void
}

export default function DeckDetail({ cardId, sourceRef, onClose }: DeckDetailProps) {
  const face = cardFace(cardId)
  const family = parseCardId(cardId)?.family ?? 1
  const patterns = patternsForCard(cardId)

  const ovRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const closingRef = useRef(false)

  const palette = {
    "--fam-glow": `var(--family-${family}-deck-glow)`,
    "--fam-ink": `var(--family-${family}-deck-ink)`,
  } as CSSProperties

  // Open: grow the card into the sheet. Layout effect so the sheet is hidden
  // before paint (no flash of it at full size before the flight).
  useLayoutEffect(() => {
    const source = sourceRef.current
    if (RM || !source || !sheetRef.current) return
    morphOpen(source, sheetRef.current, () => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close: shrink back into the card, fading the backdrop, then unmount.
  const close = () => {
    if (closingRef.current) return
    const source = sourceRef.current
    if (RM || !source || !sheetRef.current) {
      onClose()
      return
    }
    closingRef.current = true
    ovRef.current?.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 500,
      easing: "ease-in",
      fill: "forwards",
    })
    morphClose(source, sheetRef.current, onClose)
  }

  // Escape closes; the page behind must not scroll while the overlay is up.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close()
    }
    window.addEventListener("keydown", onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = previous
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Portalled to the body. The overlay is `position: fixed`, but it renders
  // inside a stacking context that sits BELOW the page chrome — AppShell's `.page`
  // (z-index 1) under `.chrome` (z-index 40), and the preview page's header does
  // the same. Trapped there, no z-index can lift it over the logo and nav: the
  // backdrop blurs the page but the mark floats on top. The portal moves it to
  // the root stacking context so it covers everything, chrome included. The morph
  // is unaffected — the sheet is viewport-fixed, so its coordinates don't move.
  return createPortal(
    // Clicking the blurred backdrop closes; clicks inside the sheet do not.
    <div
      className={styles.ov}
      style={palette}
      role="dialog"
      aria-modal="true"
      aria-label={face?.name}
      onClick={close}
      ref={ovRef}
    >
      <div className={styles.sheet} onClick={(event) => event.stopPropagation()} ref={sheetRef}>
        <button
          type="button"
          className={styles.close}
          onClick={close}
          aria-label={DECK_DETAIL_COPY.close}
        >
          <span aria-hidden="true">×</span>
        </button>

        <span className={styles.pill}>{face?.name}</span>

        <div className={styles.hero}>
          <div className={styles.heroText}>
            <h2 className={styles.statement}>{face?.statement}</h2>
            <div className={styles.tldr}>
              <div className={styles.tldrItem}>
                <b>{DECK_DETAIL_COPY.beAware}</b>
                <Skeleton />
                <Skeleton width="70%" />
              </div>
            </div>
          </div>
          <div className={styles.art}>
            <DeckCard cardId={cardId} />
          </div>
        </div>

        <section className={styles.mod}>
          <span className={styles.modPill}>{DECK_DETAIL_COPY.inAction}</span>
          {patterns ? (
            <>
              <ul className={styles.patterns}>
                {patterns.patterns.map((pattern) => (
                  <li key={pattern.id} className={styles.pattern}>
                    <PatternDemo id={pattern.id} />
                    <div className={styles.patternHead}>
                      <span className={styles.patternName}>{pattern.name}</span>
                    </div>
                    <p className={styles.patternUse}>{pattern.use}</p>
                  </li>
                ))}
              </ul>
              {patterns.fit === "partial" && patterns.fitNote ? (
                <p className={styles.fitNote}>{patterns.fitNote}</p>
              ) : null}
            </>
          ) : (
            <h3 className={styles.modHeading}>{DECK_DETAIL_COPY.patternsComingSoon}</h3>
          )}
        </section>
      </div>
    </div>,
    document.body
  )
}
